import * as HLF from "html-living-framework";
import {Application} from "../Application";
import {Ease} from "../core/Animatable";
import {Container} from "../core/Container";
import {array, Callback, Mix} from "../core/Reflection";
import {Poly} from "../core/Shape";
import {Signal, Times} from "../core/Signal";
import {Sprite} from "../core/Sprite";
import {Assets} from "../data/Assets";
import {LevelNumber} from "../data/LevelModel";
import {Location} from "../data/Location";
import {CardNumber} from "../data/LocationCardType";
import {getLocationFactVO, getLocationTileVO, voOptionsDirections} from "../data/VoiceOver";
import {AudioChannel} from "../managers/AudioChannel";
import {VoiceOverManager} from "../managers/VoiceOverManager";
import {CardTransform} from "./CardTransform";
import {MagicPoof} from "./MagicPoof";
import {PostcardItem, PostcardItemType} from "./PostcardItem";
import {SparkleBurst, SparkleBurstLarge} from "./SparkleBurst";
import {ITransformBackup, TransformBackup} from "./TransformBackup";

export class Postcard extends Mix(Container, TransformBackup) {

    public readonly background = this.addChild(new Sprite());
    public readonly content = this.addChild(new Container());
    public readonly items: PostcardItem[] = array(() => this.content.addChild(new PostcardItem(this.location, this.level)));
    public readonly shapes: number[][] = array(() => []);
    public readonly outlines: PostcardItem[] = [];
    public readonly pieces: PostcardItem[] = [];
    public readonly decorations: PostcardItem[] = [];
    public readonly cardTransform = array(() => this.addChild(new CardTransform()));
    public readonly postcardOverlay = this.addChild(new Sprite());
    public readonly postcardMask: number[] = [];
    public readonly onReveal = Signal<Callback>();
    public readonly onItemFact = Signal<Callback>();
    public readonly location: Location;
    public readonly level: LevelNumber;

    private _outlined = 0;
    private _showTransform!: ITransformBackup;
    private _hideTransform!: ITransformBackup;
    private readonly _revealingQueue: number[] = [];
    private _isRevealingPiece = false;
    private _isFlashItems = false;

    constructor(location: Location, level: LevelNumber) {
        super();

        this.location = location;
        this.level = level;
    }

    public onLayout() {

        const sort = (a: PostcardItem, b: PostcardItem) => a.postcardItemIndex - b.postcardItemIndex;
        this.pieces.push(...this.items.filter((it) => it.postcardItemType === PostcardItemType.Piece).sort(sort));
        this.outlines.push(...this.items.filter((it) => it.postcardItemType === PostcardItemType.Outline).sort(sort));
        this.decorations.push(...this.items.filter((it) => it.postcardItemType === PostcardItemType.Decoration).sort(sort));

        this._outlined = this.outlines.length;
        for (const piece of this.pieces) {
            piece.visible = false;
        }
        for (const decoration of this.decorations.filter((it) => it.dependsOn > 0)) {
            decoration.visible = false;
            if (decoration.outlineGlow) {
                for (const piece of this.pieces.filter((it) => it.postcardItemIndex === decoration.dependsOn)) {
                    piece.setOutline(decoration);
                }
            }
        }

        for (let i = 0; i < this.shapes.length; i++) {
            if (this.shapes[i] && this.shapes[i].length > 2) {
                const piece = this.pieces.filter((it) => it.postcardItemIndex === i + 1)[0];
                if (piece) {
                    piece.setShape(this.shapes[i]);
                }
            }
        }
        for (let i = 0; i < this.outlines.length; i++) {
            if (this.pieces[i] && this.outlines[i] && this.pieces[i].outlineGlow) {
                this.pieces[i].setOutline(this.outlines[i]);
            }
        }

        this.content.mask = this.addChild(new Poly(this.postcardMask));

        const size = Application.instance.resizer.getSize();
        this._showTransform = this.getTransformBackup(true);

        this._hideTransform = this.getTransformBackup(true);
        this._hideTransform.y = -size.y - this.height * 0.5;
        this._hideTransform.scaleX = 0.6;
        this._hideTransform.scaleY = 0.6;
    }

    public destroy() {
        this.onReveal.remove();
        super.destroy();
    }

    public hideByDefault() {
        this.copyTransform(this._hideTransform);
    }

    public show(cb?: Callback) {
        AudioChannel.SFX.play(Assets.audio.newPostcard.assetName);
        this.tweenTo<Postcard>(0.6, Ease.backOut, {
            ...this._showTransform,
            onComplete: cb,
        });
    }

    public hide(cb?: Callback) {
        this.tweenTo<Postcard>(0.4, Ease.sineIn, {
            ...this._hideTransform,
            onComplete: cb,
        });
    }

    public getCardTransform(index: number) {
        return this.cardTransform[index - 1];
    }

    public revealPiece(index: number) {
        // Disabled postcard revealing queue
        // if (this._isRevealingPiece) {
        //     this._revealingQueue.push(index);
        //     return;
        // }
        const piece = this.pieces.filter((it) => it.postcardItemIndex === index)[0];
        const outline = this.outlines.filter((it) => it.postcardItemIndex === index)[0];
        if (piece && outline && !piece.visible) {
            this._isRevealingPiece = true;
            AudioChannel.SFX.play(Assets.audio.itemRevealed.assetName);
            const onReveal = () => {
                this._isRevealingPiece = false;
                if (this._revealingQueue.length > 0) {
                    this.revealPiece(this._revealingQueue.shift()!);
                } else if (this._outlined <= 0) {
                    this.onReveal();
                }
            };
            const onComplete = Times(2, () => {
                onReveal();
            });
            this._outlined--;
            outline.alphaTo(0, 0.2);
            piece.visible = true;
            piece.alphaFrom(0, 0.2, onComplete);
            for (const decoration of this.decorations.filter((it) => it.dependsOn === index && !it.outlineGlow)) {
                decoration.visible = true;
                decoration.alphaFrom(0, 0.2);
            }
            const poof = this.addChild(new MagicPoof(() => {
                poof.destroy();
                onComplete();
            }));
            poof.x = piece.x;
            poof.y = piece.y;
        }
    }

    public revealItems() {
        for (const item of this.items) {
            item.visible = true;
        }
        for (const outline of this.outlines) {
            outline.visible = false;
        }
        for (const outline of this.decorations.filter((it) => it.dependsOn > 0 && it.outlineGlow)) {
            outline.visible = false;
        }
    }

    public enableItems() {
        for (const item of this.items) {
            if (item.isInteractive) {
                item.enabled = true;
                item.onOver.add(this.onOverItem, this);
                item.onOut.add(this.onOutItem, this);
                item.onClick.add(this.onClickItem, this);
            }
        }
    }

    public flashItems(exclude?: PostcardItem) {
        this._isFlashItems = true;
        const item = HLF.ArrayUtils.random(this.items.filter(
            (it) => it.isInteractive && it.postcardItemNumber > 0 && it !== exclude));
        if (item) {
            item.startGlow(true);
        }
        this.delayedCall(0.4, () => {
            if (item) {
                item.endGlow(true);
            }
        });
        this.delayedCall(0.8, () => {
            if (this._isFlashItems) {
                this.flashItems(item);
            }
        });
    }

    public stopFlashItems() {
        this._isFlashItems = false;
    }

    public sparkleItems() {
        for (const item of this.items) {
            if (item.isInteractive && item.postcardItemNumber > 0) {
                const burst = this.addChild(new SparkleBurstLarge());
                burst.x = item.x;
                burst.y = item.y;
                burst.scale.set(4);
                burst.emitter.playOnceAndDestroy();
            }
        }
    }

    private onOverItem(item: PostcardItem) {
        const index = item.postcardItemNumber;
        if (!PIXI.utils.isMobile.any && index > 0) {
            const tileVO = getLocationTileVO(this.location, this.level, index as CardNumber);
            if (tileVO) {
                VoiceOverManager.instance.playVO(tileVO, voOptionsDirections);
            }
        }
        // Glow dependent items
        for (const i of this.items) {
            if (i !== item && i.postcardItemNumber === index) {
                i.startGlow();
            }
        }
    }

    private onOutItem(item: PostcardItem) {
        // Glow dependent items
        const index = item.postcardItemNumber;
        for (const i of this.items) {
            if (i !== item && i.postcardItemNumber === index) {
                i.endGlow();
            }
        }
    }

    private onClickItem(item: PostcardItem, event?: PIXI.interaction.InteractionEvent) {
        const index = item.postcardItemNumber;
        const tileVO = getLocationTileVO(this.location, this.level, index as CardNumber);
        const factVO = getLocationFactVO(this.location, this.level, index as CardNumber);
        if (tileVO || factVO) {
            for (const i of this.items) {
                if (i.isInteractive) {
                    if (i.postcardItemNumber === index) {
                        i.startGlow(true);
                    } else {
                        i.endGlow(true);
                    }
                }
            }
            const vo = [];
            if (tileVO && PIXI.utils.isMobile.any) {
                vo.push(tileVO);
            }
            if (factVO) {
                vo.push(factVO);
            }
            const cb = (success: boolean) => {
                if (!success) {
                    return;
                }
                for (const i of this.items) {
                    if (i.postcardItemNumber === index) {
                        i.endGlow(true);
                    }
                }
                this.onItemFact();
            };
            if (VoiceOverManager.instance.active) {
                VoiceOverManager.instance.playVO(vo, {
                    ...voOptionsDirections,
                    callback: cb,
                });
            } else {
                this.delayedCall(0.8, () => {
                    cb(true);
                });
            }

            const p = event ? event.data.getLocalPosition(this) : item.position;
            const burst = this.addChild(new SparkleBurst());
            burst.x = p.x;
            burst.y = p.y;
            burst.scale.set(4);
            burst.emitter.playOnceAndDestroy();
            AudioChannel.SFX.play(Assets.audio.envelopeClick.assetName);
        }
    }
}
